import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Sparkles, ArrowRight, Star, ChevronUp, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getHistory } from '../services/cmsData';

// Individual Timeline Node with smooth animations and proper aspect ratio posters
const TimelineNode = ({ data, index, isLeft, id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-15% 0px -15% 0px" });
  
  // Alternating colors for list items
  const colors = [
    'text-white/90',
    'text-accent-gold/90',
    'text-white/80',
    'text-blue-300/90',
  ];
  
  return (
    <div 
      ref={ref}
      id={id}
      className="relative w-full scroll-mt-32"
    >
      <div className={`flex ${isLeft ? 'flex-row' : 'flex-row-reverse'} items-start gap-8 lg:gap-16`}>
        {/* Poster - Normal aspect ratio */}
        {data.image && (
          <motion.div
            initial={{ opacity: 0, x: isLeft ? -80 : 80 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isLeft ? -80 : 80 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex-shrink-0 w-[220px] lg:w-[280px]"
          >
            <motion.div
              initial={{ rotate: isLeft ? -3 : 3 }}
              animate={isInView ? { rotate: 0 } : { rotate: isLeft ? -3 : 3 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}${data.image}`} 
                alt=""
                className="w-full h-auto shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
        
        {/* Content Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex-1 min-w-0"
        >
          <div
            className={`relative overflow-hidden rounded-2xl backdrop-blur-sm border ${
              data.highlight 
                ? 'bg-accent-gold/10 border-accent-gold/30' 
                : 'bg-white/5 border-white/10'
            }`}
          >
            <div className="p-8 lg:p-10">
              {/* Highlight Badge */}
              {data.highlight && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mb-4"
                >
                  <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-gold text-brand-dark text-xs font-bold">
                    <Star className="w-3 h-3" fill="currentColor" />
                    Milestone Year
                  </span>
                </motion.div>
              )}
              
              {/* Year */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className={`font-display text-5xl lg:text-7xl font-black mb-6 leading-none ${
                  data.highlight ? 'text-accent-gold' : 'text-white'
                }`}
              >
                {data.year}
              </motion.div>
              
              {/* Events List - No bullets, alternating colors */}
              <div className="space-y-3">
                {data.events.map((event, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: isLeft ? 15 : -15 }}
                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isLeft ? 15 : -15 }}
                    transition={{ duration: 0.4, delay: 0.25 + (i * 0.08), ease: "easeOut" }}
                    className={`font-body text-sm lg:text-base leading-relaxed py-2.5 px-4 rounded-xl ${
                      i % 2 === 0 ? 'bg-white/5' : 'bg-transparent'
                    } ${colors[i % colors.length]}`}
                  >
                    {event}
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Up/Down Navigation Arrows
const TimelineNavigation = ({ onPrev, onNext, canGoPrev, canGoNext }) => {
  return (
    <div className="fixed right-6 lg:right-10 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-3">
      <button
        onClick={onPrev}
        disabled={!canGoPrev}
        className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
          canGoPrev 
            ? 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-accent-gold hover:text-brand-dark hover:border-accent-gold shadow-lg' 
            : 'bg-white/5 border border-white/10 text-white/30 cursor-not-allowed'
        }`}
        aria-label="Previous year"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
      <button
        onClick={onNext}
        disabled={!canGoNext}
        className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
          canGoNext 
            ? 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-accent-gold hover:text-brand-dark hover:border-accent-gold shadow-lg' 
            : 'bg-white/5 border border-white/10 text-white/30 cursor-not-allowed'
        }`}
        aria-label="Next year"
      >
        <ChevronDown className="w-6 h-6" />
      </button>
    </div>
  );
};

export default function HistoryPage() {
  const [timeline, setTimeline] = useState([]);
  const [preHistory, setPreHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const data = await getHistory();
        
        // Handle both old format (array) and new format (object with timeline array)
        const timelineData = Array.isArray(data) ? data : (data.timeline || []);
        
        // Store pre-history if present
        if (data.preHistory) {
          setPreHistory(data.preHistory);
        }
        
        // Combine 2015 and 2016 into a single entry
        const combined = [];
        
        for (const item of timelineData) {
          if (item.year === 2015) {
            // Find 2016 and merge
            const year2016 = timelineData.find(d => d.year === 2016);
            combined.push({
              year: "2015-16",
              events: [
                ...item.events,
                ...(year2016 ? year2016.events : [])
              ],
              highlight: item.highlight || (year2016?.highlight || false),
              image: year2016?.image || item.image
            });
          } else if (item.year === 2016) {
            // Skip, already merged
            continue;
          } else {
            combined.push(item);
          }
        }
        
        setTimeline(combined);
      } catch (error) {
        console.error('Error fetching history:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  // Track current index based on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = timeline.map((item, index) => ({
        index,
        element: document.getElementById(`year-${item.year}`)
      })).filter(s => s.element);

      const viewportMiddle = window.innerHeight / 2;
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const rect = section.element.getBoundingClientRect();
        if (rect.top <= viewportMiddle && rect.bottom >= viewportMiddle) {
          setCurrentIndex(section.index);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [timeline]);

  const navigatePrev = () => {
    if (currentIndex > 0) {
      const prevYear = timeline[currentIndex - 1]?.year;
      const element = document.getElementById(`year-${prevYear}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const navigateNext = () => {
    if (currentIndex < timeline.length - 1) {
      const nextYear = timeline[currentIndex + 1]?.year;
      const element = document.getElementById(`year-${nextYear}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  return (
    <div data-testid="history-page" className="min-h-screen bg-brand-dark">
      {/* Up/Down Navigation Arrows */}
      {!loading && timeline.length > 0 && (
        <TimelineNavigation 
          onPrev={navigatePrev}
          onNext={navigateNext}
          canGoPrev={currentIndex > 0}
          canGoNext={currentIndex < timeline.length - 1}
        />
      )}

      {/* Hero - Dark and immersive */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-20 right-1/4 w-[600px] h-[600px] bg-brand-primary/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-accent-gold/10 rounded-full blur-[120px]" />
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-10 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur-sm"
            >
              <Sparkles className="w-10 h-10 text-accent-gold" />
            </motion.div>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="font-accent text-accent-gold/80 text-sm tracking-[0.4em] mb-6 uppercase"
            >
              Our Journey
            </motion.p>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight"
            >
              The Story<br />
              <span className="text-accent-gold">So Far</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.7 }}
              className="font-body text-white/60 text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed"
            >
              From our first show in 2013 to today's spectacular productions — 
              explore over a decade of bringing joy to families across the UK.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section ref={containerRef} className="py-16 lg:py-24 relative overflow-hidden">
        {/* Ambient background glows */}
        <motion.div 
          className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-brand-primary/10 rounded-full blur-[100px] pointer-events-none"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, 200]) }}
        />
        <motion.div 
          className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-accent-gold/5 rounded-full blur-[120px] pointer-events-none"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, -150]) }}
        />
        
        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative">
          {loading ? (
            <div className="space-y-24">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-12">
                  <div className="w-[250px] h-[350px] bg-white/5 rounded-xl animate-pulse" />
                  <div className="flex-1 h-[300px] bg-white/5 rounded-2xl animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-20 lg:space-y-28">
              {timeline.map((item, index) => (
                <TimelineNode 
                  key={item.year} 
                  data={item} 
                  index={index} 
                  isLeft={index % 2 === 0}
                  id={`year-${item.year}`}
                />
              ))}
            </div>
          )}
          
          {/* Pre-History Section */}
          {preHistory && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="mt-32 max-w-4xl mx-auto"
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-10 lg:p-14">
                <div className="text-center mb-8">
                  <span className="font-display text-5xl lg:text-6xl font-black text-white/30">
                    {preHistory.years}
                  </span>
                </div>
                <p className="font-body text-white/70 text-lg leading-relaxed text-center">
                  {preHistory.description}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-accent-gold/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-brand-secondary/20 rounded-full blur-[120px]" />
        
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
              Be Part of Our<br />
              <span className="text-accent-gold">Next Chapter</span>
            </h2>
            <p className="font-body text-white/60 text-lg lg:text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
              With exciting new productions launching every year, there's never been 
              a better time to experience the magic of live family theatre.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link
                to="/shows"
                data-testid="see-shows-btn"
                className="group inline-flex items-center justify-center gap-3 text-brand-dark px-10 py-5 rounded-full font-bold text-lg transition-all duration-500 hover:bg-white hover:scale-105"
                style={{ backgroundColor: '#D4AF37' }}
              >
                See Our Shows
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/tour-dates"
                data-testid="find-dates-btn"
                className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-10 py-5 rounded-full font-semibold text-lg transition-all duration-500 hover:bg-white/10 hover:border-white/40"
              >
                Find Tour Dates
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
